import React, { useState, useImperativeHandle } from 'react';
import { useDispatch } from 'react-redux';

import { Currency } from '@web-solutions/react-billing/constants';
import { SolidMetadata } from '@web-solutions/core/interfaces/billing';

import { createOneClickPayment } from 'core/store/billing/actions';

import { setIsLoading } from 'core/upsale/store';

import { Verify3dsModal } from '../../containers/verify-3ds';

interface OneClickPaymentArgs {
  amount: number;
  currency: Currency;
  productCode: string;
  trigger: string;
  productId: string;
  force3ds?: boolean;
  metadata?: any;
  solidMetadata?: SolidMetadata;
  withTransactionDetailsEmail?: boolean;
}

export interface OneClickPaymentRef {
  createOneClickPayment: (args: OneClickPaymentArgs) => Promise<void>;
}

interface OneClickPaymentProps {
  orderDetails: any;
  onSuccess: (data?: any) => void;
  onFail: (error: Error) => void;
}

export const OneClickPayment = React.forwardRef<OneClickPaymentRef, OneClickPaymentProps>(
  ({ onFail, onSuccess, orderDetails }, ref) => {
    const dispatch = useDispatch();
    const [verifyUrl, setVerifyUrl] = useState('');
    const [_, setPending] = useState(false);
    const [data, setData] = useState();

    useImperativeHandle(ref, () => {
      const handleSubmit = async ({
        amount,
        currency,
        productCode,
        productId,
        trigger,
        force3ds,
        metadata,
        solidMetadata,
        withTransactionDetailsEmail,
      }: OneClickPaymentArgs) => {
        try {
          dispatch(setIsLoading(true));
          setPending(true);
          const result = (await dispatch(
            createOneClickPayment({
              force3ds,
              amount,
              currency,
              productCode,
              productId,
              trigger,
              metadata,
              solidMetadata,
              withTransactionDetailsEmail,
              transactionId:
                orderDetails?.products?.[productCode]?.transactionId ||
                orderDetails?.one_time_products?.[productCode]?.transactionId ||
                orderDetails?.transactionId
            })
          )) as any;
          const url = result?.verify_url;

          if (url) {
            setVerifyUrl(url);
            setData(result?.one_time_payment ?? result);
          } else {
            onSuccess(result?.one_time_payment ?? result);
            setVerifyUrl('');
          }
          setPending(false);
        } catch (error: any) {
          setPending(false);
          onFail(error);
        }
      };
      return {
        createOneClickPayment: handleSubmit,
      };
    }, [dispatch, onSuccess, onFail]);

    const handleFail = () => {
      onFail(Error('3ds-error'));
      setVerifyUrl('');
    };

    const handleSuccess = () => {
      onSuccess(data);
      setVerifyUrl('');
    };

    return <Verify3dsModal verifyUrl={verifyUrl} onSuccess={handleSuccess} onFail={handleFail} />;
  }
);
